import CheckCircle from "@mui/icons-material/CheckCircle";
import Dangerous from "@mui/icons-material/Dangerous";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Help from "@mui/icons-material/Help";
import InsertDriveFile from "@mui/icons-material/InsertDriveFile";
import ModeEdit from "@mui/icons-material/ModeEdit";
import Visibility from "@mui/icons-material/Visibility";
import Warning from "@mui/icons-material/Warning";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import _ from "lodash";
import { useState } from "react";
import { CorePipelineOutput, ValidationResult } from "../../interfaces/doc";
import CoreDisplay from "./CoreDisplay";

interface DocumentDisplayProps {
  title?: string | null;
  coreResult: CorePipelineOutput;
  validation: Record<string, ValidationResult> | null | undefined;
  setPdf: Function;
  toEdit: Function;
}

const DocumentDisplay: React.FC<DocumentDisplayProps> = ({
  title,
  coreResult,
  validation,
  setPdf,
  toEdit,
}) => {
  const [expanded, setExpanded] = useState(false);

  const toggleAccordion = () => {
    setExpanded(!expanded);
  };

  const valid = !!validation
    ? _.entries(validation).some(([_, result]) => result.success === false)
      ? false
      : _.entries(validation).every(([_, result]) => result.success === true)
        ? true
        : undefined
    : null;

  const hasResult = !_.values(coreResult.extraction).every(
    (e) => e.value === null || e.value === undefined || _.isEmpty(e.value)
  );

  return (
    <Paper
      square={false}
      elevation={3}
      className="flex flex-col gap-2 p-2 border-[var(--mui-palette-primary-main)] border-solid border-1"
    >
      <div
        className="p-4 flex items-center justify-between cursor-pointer"
        onClick={() => hasResult && toggleAccordion()}
      >
        <div className="flex justify-between w-full items-center">
          <div className="flex items-center gap-4">
            <InsertDriveFile className="text-gray-400" />
            <div className="flex flex-col">
              {!!title && (
                <Typography className="font-bold">{title}</Typography>
              )}
              <div className="flex">
                {coreResult &&
                  coreResult.classification &&
                  Object.values(coreResult.classification).map(
                    (result, index) => <Chip key={index} label={result} />
                  )}
              </div>
            </div>
          </div>
          <div className="flex items-center">
            {!hasResult && (
              <Tooltip
                slotProps={{
                  tooltip: {
                    className: "bg-[#323639] p-2",
                  },
                }}
                title="Aucune donnée n'a pu être extraite"
                placement="top"
              >
                <Dangerous color="error" className="mr-[8px]" />
              </Tooltip>
            )}
            {valid === false && hasResult && (
              <Tooltip
                slotProps={{
                  tooltip: {
                    className: "bg-[#323639] p-2",
                  },
                }}
                title="Le document ne respecte pas les règles de validation"
                placement="top"
              >
                <Warning color="warning" className="mr-[8px]" />
              </Tooltip>
            )}
            {valid === undefined && (
              <Tooltip
                slotProps={{
                  tooltip: {
                    className: "bg-[#323639] p-2",
                  },
                }}
                title="Certaines règles de validation n'ont pas pu être vérifiées"
                placement="top"
              >
                <Help color="disabled" className="mr-[8px]" />
              </Tooltip>
            )}
            {valid === true && (
              <Tooltip
                slotProps={{
                  tooltip: {
                    className: "bg-[#323639] p-2",
                  },
                }}
                title="Le document respecte les règles de validation"
                placement="top"
              >
                <CheckCircle color="success" className="mr-[8px]" />
              </Tooltip>
            )}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                toEdit();
              }}
            >
              <ModeEdit />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setPdf();
              }}
            >
              <Visibility />
            </IconButton>
            <IconButton sx={{ visibility: hasResult ? "visible" : "hidden" }}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </div>
        </div>
      </div>
      {hasResult && expanded && (
        <CoreDisplay coreResult={coreResult} validation={validation} />
      )}
    </Paper>
  );
};

export default DocumentDisplay;
